import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, FileText, Package, TrendingUp, DollarSign, CreditCard, Video, BookOpen, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { usersAPI, promptsAPI, packagesAPI, videosAPI, booksAPI } from '../../services/api';

const AdminDashboard = () => {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalPrompts: 0,
    activePackages: 0,
    totalVideos: 0,
    totalBooks: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const [usersRes, promptsRes, packagesRes, videosRes, booksRes] = await Promise.allSettled([
        usersAPI.getAll(),
        promptsAPI.getAll({}),
        packagesAPI.getAll(false),
        videosAPI.getAllAdmin(),
        booksAPI.getAll(),
      ]);

      setStats({
        totalUsers: usersRes.status === 'fulfilled' ? (usersRes.value.data?.length || 0) : 0,
        totalPrompts: promptsRes.status === 'fulfilled' ? (promptsRes.value.data?.length || 0) : 0,
        activePackages: packagesRes.status === 'fulfilled' ? (packagesRes.value.data?.filter(p => p.is_active)?.length || 0) : 0,
        totalVideos: videosRes.status === 'fulfilled' ? (videosRes.value.data?.length || 0) : 0,
        totalBooks: booksRes.status === 'fulfilled' ? (booksRes.value.data?.length || 0) : 0,
      });
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      setError('Failed to load dashboard statistics');
    } finally {
      setIsLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Users', value: stats.totalUsers, icon: Users, color: 'text-blue-400' },
    { title: 'Total Prompts', value: stats.totalPrompts, icon: FileText, color: 'text-purple-400' },
    { title: 'Active Packages', value: stats.activePackages, icon: Package, color: 'text-green-400' },
    { title: 'Total Videos', value: stats.totalVideos, icon: Video, color: 'text-red-400' },
    { title: 'Total Books', value: stats.totalBooks, icon: BookOpen, color: 'text-yellow-400' },
  ];

  const quickLinks = [
    { title: 'Manage Users', description: 'View, edit, and manage user accounts', icon: Users, path: '/admin/users', color: 'from-blue-600 to-blue-800' },
    { title: 'Manage Prompts', description: 'Create, edit, and organize prompts', icon: FileText, path: '/admin/prompts', color: 'from-purple-600 to-purple-800' },
    { title: 'Manage Packages', description: 'Configure pricing packages', icon: Package, path: '/admin/packages', color: 'from-green-600 to-green-800' },
    { title: 'Payments & Promos', description: 'Verify payments & promo codes', icon: CreditCard, path: '/admin/payments', color: 'from-yellow-600 to-yellow-800' },
    { title: 'Manage Videos', description: 'Add and edit training videos', icon: Video, path: '/admin/videos', color: 'from-red-600 to-red-800' },
    { title: 'Manage Books', description: 'Manage books and access control', icon: BookOpen, path: '/admin/books', color: 'from-orange-600 to-orange-800' },
  ];

  // Show loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-gray-400">Welcome back, {user?.name || 'Admin'}</p>
        </div>
        <div className="flex justify-center py-12">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
        <p className="text-gray-400">Welcome back, {user?.name || 'Admin'}</p>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-red-500" size={20} />
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="bg-[#1a1a2e] border-gray-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">{stat.title}</p>
                  <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-lg bg-gray-800 flex items-center justify-center ${stat.color}`}>
                  <stat.icon size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Links */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickLinks.map((link, index) => (
            <Link key={index} to={link.path}>
              <Card className="bg-[#1a1a2e] border-gray-800 hover:border-purple-500/50 transition-all cursor-pointer h-full">
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${link.color} flex items-center justify-center mb-4`}>
                    <link.icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">{link.title}</h3>
                  <p className="text-gray-400 text-sm">{link.description}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <Card className="bg-[#1a1a2e] border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <TrendingUp size={20} />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              { action: 'Dashboard loaded', user: user?.email || 'Admin', time: 'Just now' },
              { action: `${stats.totalUsers} users in system`, user: 'System', time: 'Current' },
              { action: `${stats.totalPrompts} prompts available`, user: 'System', time: 'Current' },
              { action: `${stats.totalBooks} books in library`, user: 'System', time: 'Current' },
            ].map((activity, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0">
                <div>
                  <p className="text-white">{activity.action}</p>
                  <p className="text-gray-500 text-sm">{activity.user}</p>
                </div>
                <p className="text-gray-400 text-sm">{activity.time}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
